import { MessageType } from '@chessclub/proto-ts/index.websocket_gateway'
import { isSameDay } from 'date-fns/isSameDay'
import { parseISO } from 'date-fns/parseISO'
import { Action, action, thunk, Thunk } from 'easy-peasy'
import { NavigateFunction } from 'react-router-dom'
import { nameOfSquare, pieceSymbol } from '../../../chess/basics'
import { positionToFEN } from '../../../chess/fen'
import {
    addMoveToGTPosition,
    createRootGameTree,
    getMoveById,
    getPositionById,
    getStartingPosition,
    undoLastMove,
} from '../../../chess/gameTree'
import { createPuzzlePGNString, gameFromPGN } from '../../../chess/pgn'
import { BasePositionFEN } from '../../../chess/positionPresets'
import { Color, GameTree, GameTreePosition, Move } from '../../../chess/types'
import { ENABLE_DAILY_PUZZLE_FEEDBACK } from '../../../env'
import { Arrow, Square } from '../../../react-chessboard/src/chessboard/types'
import { GroupNames } from '../../../sharedComponents/src/globalHeader/GlobalHeader'
import { analyticsManager } from '../../../sharedComponents/src/globalHeader/services/analytics/AnalyticsManager'
import { ServerErrors } from '../../../sharedComponents/src/utils/serverErrors'
import { StoreModel } from '../../../store/store'
import {
    getDailyPuzzle,
    ITag,
    PuzzleStatus,
    resolveDailyPuzzle,
    usedHint,
} from '../../PuzzlesView/functions/puzzlesApi'
import getParsedDate from '../../PuzzlesView/helpers/getParsedDate'
import navigateToDate from '../../PuzzlesView/helpers/navigateToDate'
import { NotationsType } from '../components/Notations'
import { FeedbackType } from '../helpers/getFeedbackMessage'

const BOT_MOVING_DELAY = 1000 // 1 sec
const RETRY_MOVE_DELAY = 2500 // 2.5 sec

interface IPuzzle {
    id: string
    pgnString: string
    fen: string
    rating: number
    description: string
    isFirstMove: boolean
    date: Date
}

type DailyStreak = {
    currentStreak: number
    currentPuzzleResolved: boolean
}

export type DailyPuzzleV2Model = {
    // State
    puzzleId: string
    puzzle?: IPuzzle
    progressGameTree: GameTree
    progressCurrentPosition: GameTreePosition
    gameTree: GameTree
    currentPosition: GameTreePosition
    myColor: Color
    flipped: boolean
    colorToMove: number
    userRating: number
    hint?: Square
    hintsCounter: number
    availableHints: number
    customArrows: Array<Arrow>
    streak: DailyStreak
    puzzleState: boolean | null
    opponentMoveTimeout?: NodeJS.Timeout
    retryMoveTimeout?: NodeJS.Timeout
    attemptsCounter: number
    joinUsOpen: boolean
    showHintLimit: boolean
    notations: Array<NotationsType>
    showSolution: boolean
    usedShowSolution: boolean
    showStreak: boolean
    showStreakAnimation: boolean
    showFeedbackDialog: boolean
    feedbackMessage: string
    feedbackType?: FeedbackType
    showHintLimitDialog: boolean
    previousPuzzleAvailable: boolean
    nextPuzzleAvailable: boolean
    performanceGroupID: number

    // Actions
    setGameTree: Action<DailyPuzzleV2Model, GameTree>
    setProgressCurrentPosition: Action<DailyPuzzleV2Model, GameTreePosition>
    setCurrentPosition: Action<DailyPuzzleV2Model, GameTreePosition>
    setFlipBoard: Action<DailyPuzzleV2Model, boolean>
    initialiseGame: Action<
        DailyPuzzleV2Model,
        { puzzleId: string; gameTree?: GameTree | undefined; isFirstMove?: boolean | undefined }
    >
    setColorToMove: Action<DailyPuzzleV2Model, number>
    undoLastMove: Action<DailyPuzzleV2Model, GameTree>
    resetGame: Action<DailyPuzzleV2Model>
    setUserRating: Action<DailyPuzzleV2Model, number>
    setHint: Action<DailyPuzzleV2Model, { from: Square; to?: Square } | undefined>
    clearHint: Action<DailyPuzzleV2Model>
    setAvailableHints: Action<DailyPuzzleV2Model, number>
    setPuzzle: Action<DailyPuzzleV2Model, IPuzzle | undefined>
    setStreak: Action<DailyPuzzleV2Model, DailyStreak>
    setPuzzleState: Action<DailyPuzzleV2Model, boolean | null>
    setOpponentMoveTimeout: Action<DailyPuzzleV2Model, NodeJS.Timeout>
    setRetryMoveTimeout: Action<DailyPuzzleV2Model, NodeJS.Timeout>
    setAttemptsCounter: Action<DailyPuzzleV2Model, number>
    setJoinUsOpen: Action<DailyPuzzleV2Model, boolean>
    setShowHintLimit: Action<DailyPuzzleV2Model, boolean>
    setNotations: Action<DailyPuzzleV2Model, Array<NotationsType>>
    updateNotations: Action<DailyPuzzleV2Model, NotationsType>
    setShowSolution: Action<DailyPuzzleV2Model, boolean>
    setUsedShowSolution: Action<DailyPuzzleV2Model, boolean>
    setShowStreak: Action<DailyPuzzleV2Model, boolean>
    setShowStreakAnimation: Action<DailyPuzzleV2Model, boolean>
    setShowFeedbackDialog: Action<DailyPuzzleV2Model, boolean>
    setFeedbackMessage: Action<DailyPuzzleV2Model, string>
    clearAllTimers: Action<DailyPuzzleV2Model>
    setFeedbackType: Action<DailyPuzzleV2Model, FeedbackType | undefined>
    setShowHintLimitDialog: Action<DailyPuzzleV2Model, boolean>
    setValidations: Action<DailyPuzzleV2Model, { prevDate: boolean; nextDate: boolean }>
    setPerformanceGroupID: Action<DailyPuzzleV2Model, number>

    // Thunks
    getPuzzle: Thunk<DailyPuzzleV2Model, { navigate: NavigateFunction; date: string | undefined }>
    sendPuzzle: Thunk<DailyPuzzleV2Model>
    setHintAsync: Thunk<DailyPuzzleV2Model, { from: Square; to?: Square } | undefined>
    handleMove: Thunk<DailyPuzzleV2Model, Move>
    autoMove: Thunk<DailyPuzzleV2Model, boolean | undefined>
    retryMove: Thunk<DailyPuzzleV2Model>
    startPuzzle: Thunk<DailyPuzzleV2Model>
    handleHint: Thunk<DailyPuzzleV2Model, GroupNames | 'visitor'>
    resetAllStates: Thunk<DailyPuzzleV2Model>
    establishListener: Thunk<DailyPuzzleV2Model, undefined, undefined, StoreModel>
}

const initialGameTree = createRootGameTree(BasePositionFEN)
const initialCurrentPosition = getStartingPosition(initialGameTree)

export const DailyPuzzleViewV2Model: DailyPuzzleV2Model = {
    // Default state
    puzzleId: '',
    myColor: Color.White,
    flipped: false,
    progressGameTree: initialGameTree,
    progressCurrentPosition: initialCurrentPosition,
    gameTree: initialGameTree,
    currentPosition: initialCurrentPosition,
    colorToMove: 1,
    userRating: 0,
    hintsCounter: 0,
    availableHints: -1,
    customArrows: [],
    streak: { currentStreak: 0, currentPuzzleResolved: false },
    puzzleState: null,
    opponentMoveTimeout: undefined,
    retryMoveTimeout: undefined,
    attemptsCounter: 1,
    joinUsOpen: false,
    showHintLimit: false,
    notations: [],
    showSolution: false,
    usedShowSolution: false,
    showStreak: false,
    showStreakAnimation: false,
    showFeedbackDialog: false,
    feedbackMessage: '',
    showHintLimitDialog: false,
    previousPuzzleAvailable: true,
    nextPuzzleAvailable: false,
    performanceGroupID: 0,

    // Actions
    setGameTree: action((state, payload) => {
        state.gameTree = payload
        state.currentPosition = getStartingPosition(payload)
    }),

    setProgressCurrentPosition: action((state, payload) => {
        state.progressCurrentPosition = payload
    }),

    setCurrentPosition: action((state, payload) => {
        state.currentPosition = payload
    }),

    setFlipBoard: action((state, flipped) => {
        state.flipped = flipped
    }),

    initialiseGame: action((state, gd) => {
        const newPuzzleGameTree = gd.gameTree || initialGameTree
        state.progressGameTree = newPuzzleGameTree
        const startingProgressPosition = getStartingPosition(newPuzzleGameTree)
        state.progressCurrentPosition = startingProgressPosition

        const newGameTree = createRootGameTree(positionToFEN(startingProgressPosition.position))
        state.gameTree = newGameTree
        state.currentPosition = getStartingPosition(newGameTree)

        if (gd.isFirstMove) {
            state.myColor = startingProgressPosition.position.turn
        } else if (startingProgressPosition.position.turn === Color.Black) {
            state.myColor = Color.White
        } else {
            state.myColor = Color.Black
        }

        state.flipped = state.myColor === Color.Black
        state.puzzleId = gd.puzzleId
    }),

    setColorToMove: action((state, number) => {
        state.colorToMove = number
    }),

    undoLastMove: action((state, payload) => {
        const previousPosition = undoLastMove(payload)
        state.currentPosition = previousPosition || initialCurrentPosition
        state.gameTree = payload
        state.notations = state.notations.slice(0, -1)
    }),

    resetGame: action((state) => {
        state.myColor = Color.White
        state.progressGameTree = initialGameTree
        state.progressCurrentPosition = initialCurrentPosition
        state.gameTree = initialGameTree
        state.currentPosition = initialCurrentPosition
        state.colorToMove = 1
        state.puzzleId = ''
    }),

    setUserRating: action((state, rating) => {
        state.userRating = rating
    }),

    setHint: action((state, payload) => {
        if (payload) {
            state.hint = payload.from
            state.hintsCounter += 1
            if (payload.to) {
                state.customArrows.push([payload.from, payload.to, '#C9F1AD', 0.8])
            }
        } else {
            state.hint = undefined
            state.customArrows = []
        }
    }),

    clearHint: action((state) => {
        state.hint = undefined
        state.hintsCounter = 0
        state.customArrows = []
    }),

    setAvailableHints: action((state, payload) => {
        state.availableHints = payload
    }),

    setPuzzle: action((state, payload) => {
        state.puzzle = payload
    }),

    setStreak: action((state, payload) => {
        state.streak = payload
    }),

    setPuzzleState: action((state, payload) => {
        state.puzzleState = payload
    }),

    setOpponentMoveTimeout: action((state, payload) => {
        state.opponentMoveTimeout = payload
    }),

    setRetryMoveTimeout: action((state, payload) => {
        state.retryMoveTimeout = payload
    }),

    setAttemptsCounter: action((state, payload) => {
        state.attemptsCounter = payload
    }),

    setJoinUsOpen: action((state, payload) => {
        state.joinUsOpen = payload
    }),

    setShowHintLimit: action((state, payload) => {
        state.showHintLimit = payload
    }),

    setNotations: action((state, payload) => {
        state.notations = payload
    }),

    updateNotations: action((state, payload) => {
        state.notations = state.notations.concat(payload)
    }),

    setShowSolution: action((state, payload) => {
        state.showSolution = payload
    }),

    setUsedShowSolution: action((state, payload) => {
        state.usedShowSolution = payload
    }),

    setShowStreak: action((state, payload) => {
        state.showStreak = payload
    }),

    setShowStreakAnimation: action((state, payload) => {
        state.showStreakAnimation = payload
    }),

    setShowFeedbackDialog: action((state, payload) => {
        state.showFeedbackDialog = payload
    }),

    setFeedbackMessage: action((state, payload) => {
        state.feedbackMessage = payload
    }),

    clearAllTimers: action((state) => {
        if (state.opponentMoveTimeout) {
            clearTimeout(state.opponentMoveTimeout)
            state.opponentMoveTimeout = undefined
        }
        if (state.retryMoveTimeout) {
            clearTimeout(state.retryMoveTimeout)
            state.retryMoveTimeout = undefined
        }
    }),

    setFeedbackType: action((state, payload) => {
        state.feedbackType = payload
    }),

    setShowHintLimitDialog: action((state, payload) => {
        state.showHintLimitDialog = payload
    }),

    setValidations: action((state, payload) => {
        state.previousPuzzleAvailable = payload.prevDate
        state.nextPuzzleAvailable = payload.nextDate
    }),

    setPerformanceGroupID: action((state, payload) => {
        state.performanceGroupID = payload
    }),

    // Thunks
    setHintAsync: thunk(async (actions, payload, helpers) => {
        try {
            const { getState } = helpers
            const state = getState()
            if (state.availableHints !== 0) {
                const result = await usedHint()
                actions.setHint(payload)
                actions.setAvailableHints(result.availableHints)
                analyticsManager.dispatchEvent('powerUpUsed', {
                    type: 'hints',
                    oldArgument: state.availableHints,
                    newArgument: result.availableHints,
                    argumentChange: state.availableHints - result.availableHints,
                    feature: 'dailyPuzzle',
                    puzzleId: state.puzzleId || '',
                })
            }
        } catch (error) {
            console.error('[DAILY PUZZLE] Error in setHintAsync:', error)
        }
    }),

    getPuzzle: thunk(async (actions, payload) => {
        try {
            const { navigate, date } = payload
            const parsedDate = getParsedDate(date)
            const puzzleData = await getDailyPuzzle(parsedDate)

            if (puzzleData === null) {
                navigateToDate(navigate)
                return
            }

            if (puzzleData?.puzzle && puzzleData.date) {
                const puzzleDate = getParsedDate(puzzleData.date)
                const currentDate = date ? getParsedDate(date) : new Date()
                if (!isSameDay(currentDate, puzzleDate)) {
                    navigateToDate(navigate, puzzleDate)
                    return
                }
                const puzzle = puzzleData.puzzle
                const pgnString = createPuzzlePGNString(puzzle.startingFen, puzzle.moves)

                actions.setUserRating(puzzleData.userPuzzleRating || 0)
                actions.setPuzzle({
                    id: puzzle.id,
                    pgnString: pgnString,
                    fen: puzzle.startingFen,
                    rating: puzzle.rating,
                    description: puzzle.description,
                    isFirstMove: puzzle.isFirstMove,
                    // BE sends 'yyyy-MM-dd HH:mm:ss'; JavaScriptCore <= 15 parses that as Invalid Date
                    date: parseISO(puzzleData.date),
                })

                if (puzzleData.streak) {
                    actions.setStreak(puzzleData.streak)
                }

                if (puzzleData.options) {
                    actions.setAvailableHints(puzzleData.options.availableHints)
                }

                if (
                    puzzleData?.nextPuzzleAvailable !== undefined &&
                    puzzleData?.previousPuzzleAvailable !== undefined
                ) {
                    actions.setValidations({
                        prevDate: puzzleData.previousPuzzleAvailable,
                        nextDate: puzzleData.nextPuzzleAvailable,
                    })
                }

                actions.startPuzzle()

                analyticsManager.dispatchEvent('startedPuzzle', {
                    puzzleId: puzzle.id,
                    puzzleRating: puzzle.rating,
                    puzzleTags: puzzle.tags?.map((tag: ITag) => tag.name).join(', ') || '',
                    origin: 'dailyPuzzle',
                    userPuzzleRating: puzzleData.userPuzzleRating || 0,
                })
            }

            if (puzzleData?.error?.code === ServerErrors.DAILY_PUZZLE_LIMIT_REACHED) {
                actions.setJoinUsOpen(true)
            }
        } catch (error) {
            console.error('[DAILY PUZZLE] Error in getPuzzle:', error)
        }
    }),

    sendPuzzle: thunk(async (actions, _payload, { getState }) => {
        const state = getState()
        if (state.puzzle) {
            let status = PuzzleStatus.FAILED
            if (state.usedShowSolution) {
                status = PuzzleStatus.SHOWED
            } else if (state.puzzleState === true) {
                status = PuzzleStatus.PASSED
            } else if (state.puzzleState === null && state.hintsCounter === 0 && state.attemptsCounter === 1) {
                status = PuzzleStatus.SKIPPED
            }

            const data = await resolveDailyPuzzle({
                puzzleId: state.puzzle.id,
                status: status,
                usedHints: state.hintsCounter,
                attempts: state.attemptsCounter,
            })

            if (data.result) {
                analyticsManager.dispatchEvent('completedPuzzle', {
                    origin: 'dailyPuzzle',
                    puzzleId: state.puzzle.id,
                    puzzleRating: state.puzzle.rating,
                    puzzleSource: 0,
                    userPuzzleRating: state.userRating,
                    result: status,
                    attempts: state.attemptsCounter,
                    usedHints: state.hintsCounter,
                    datePlayed: new Date().toISOString(),
                })

                actions.setStreak(data.streak)
                actions.setUserRating(data.userPuzzleRating || 0)
            }
        }
    }),

    handleMove: thunk((actions, move, { getState }) => {
        const state = getState()

        // if it not my turn, no interaction
        if (state.currentPosition.position.turn !== state.myColor) return false

        const expectedProgressMove = getMoveById(state.progressGameTree, state.progressCurrentPosition?.nextMoveIds[0])
        if (!expectedProgressMove) return false

        const isMoveCorrect =
            move.from === expectedProgressMove.move.from &&
            move.to === expectedProgressMove.move.to &&
            move.promotion === expectedProgressMove.move.promotion

        if (isMoveCorrect) {
            const nextProgressPosition = getPositionById(state.progressGameTree, expectedProgressMove.nextPositionId)
            actions.setProgressCurrentPosition(nextProgressPosition)
            const nextPosition = addMoveToGTPosition(state.gameTree, state.currentPosition, expectedProgressMove.move)
            actions.setCurrentPosition(nextPosition)

            const actualMove = getMoveById(state.gameTree, nextPosition.previousMoveId!)
            if (actualMove) {
                const moveFromSquare = nameOfSquare(actualMove.move.from)
                actions.updateNotations({
                    location: nameOfSquare(actualMove.move.to),
                    id: actualMove.id,
                    piece: pieceSymbol(state.currentPosition.position, actualMove.move.from),
                    playersMove: true,
                    usedHint: !!state.hint && state.hint === moveFromSquare,
                    usedShowSolution: false,
                    isLost: false,
                    isCorrect: true,
                    nextPositionId: nextPosition.id,
                })
            }

            if (nextProgressPosition.nextMoveIds.length === 0) {
                actions.setHint(undefined)
                actions.setPuzzleState(true)
                actions.setFeedbackType(FeedbackType.PUZZLE_SOLVED)
                actions.sendPuzzle()
            } else {
                actions.setHint(undefined)
                actions.setFeedbackType(FeedbackType.CORRECT_MOVE)
                const timeout = setTimeout(() => {
                    actions.autoMove()
                }, BOT_MOVING_DELAY)
                actions.setOpponentMoveTimeout(timeout)
            }

            return true
        }

        const nextPosition = addMoveToGTPosition(state.gameTree, state.currentPosition, move)
        actions.setCurrentPosition(nextPosition)

        const actualMove = getMoveById(state.gameTree, nextPosition.previousMoveId!)
        if (actualMove) {
            const moveFromSquare = nameOfSquare(actualMove.move.from)
            actions.updateNotations({
                location: nameOfSquare(actualMove.move.to),
                id: actualMove.id,
                piece: pieceSymbol(state.currentPosition.position, actualMove.move.from),
                playersMove: true,
                usedHint: !!state.hint && state.hint === moveFromSquare,
                usedShowSolution: false,
                isLost: true,
                isCorrect: false,
                nextPositionId: nextPosition.id,
            })
        }

        actions.setPuzzleState(false)
        actions.setFeedbackType(FeedbackType.INCORRECT_MOVE)
        actions.retryMove()
        return false
    }),

    autoMove: thunk((actions, showSolution = false, { getState }) => {
        const state = getState()
        const nextProgressMove = getMoveById(state.progressGameTree, state.progressCurrentPosition?.nextMoveIds[0])
        if (nextProgressMove) {
            const nextProgressPosition = getPositionById(state.progressGameTree, nextProgressMove.nextPositionId)
            actions.setProgressCurrentPosition(nextProgressPosition)
            const nextPosition = addMoveToGTPosition(state.gameTree, state.currentPosition, nextProgressMove.move)
            actions.setCurrentPosition(nextPosition)

            const actualMove = getMoveById(state.gameTree, nextPosition.previousMoveId!)
            if (actualMove) {
                const playersMove = state.currentPosition.position.turn === state.myColor
                const moveFromSquare = nameOfSquare(actualMove.move.from)
                actions.updateNotations({
                    location: nameOfSquare(actualMove.move.to),
                    id: actualMove.id,
                    piece: pieceSymbol(state.currentPosition.position, actualMove.move.from),
                    playersMove: playersMove,
                    usedHint: !!state.hint && state.hint === moveFromSquare,
                    usedShowSolution: playersMove && showSolution,
                    isLost: false,
                    isCorrect: playersMove && state.puzzleState !== false,
                    nextPositionId: nextPosition.id,
                })
            }

            if (showSolution) {
                actions.setHint(undefined)
                if (!state.showSolution) {
                    actions.setFeedbackType(FeedbackType.SOLUTION_SHOWN)
                }
                actions.setShowSolution(true)
                actions.setUsedShowSolution(true)
                const nextProgressPosition = getPositionById(state.progressGameTree, nextProgressMove.nextPositionId)
                if (nextProgressPosition.nextMoveIds.length === 0) {
                    actions.setPuzzleState(true)
                    actions.setShowSolution(false)
                    actions.setFeedbackType(FeedbackType.PUZZLE_SOLVED)
                    actions.sendPuzzle()
                } else {
                    const timeout = setTimeout(() => {
                        actions.autoMove(showSolution)
                    }, BOT_MOVING_DELAY)
                    actions.setOpponentMoveTimeout(timeout)
                }
            } else if (nextProgressPosition.position.turn === state.myColor) {
                const nextProgressPosition = getPositionById(state.progressGameTree, nextProgressMove.nextPositionId)
                if (nextProgressPosition.nextMoveIds.length === 0) {
                    actions.setPuzzleState(true)
                    actions.setFeedbackType(FeedbackType.PUZZLE_SOLVED)
                    actions.sendPuzzle()
                }
            }
        }
    }),

    retryMove: thunk((actions, payload, { getState }) => {
        const state = getState()
        actions.setAttemptsCounter(state.attemptsCounter + 1)
        const timeout = setTimeout(() => {
            actions.undoLastMove(state.gameTree)
            actions.setPuzzleState(null)
        }, RETRY_MOVE_DELAY)
        actions.setRetryMoveTimeout(timeout)
    }),

    startPuzzle: thunk((actions, _payload, { getState }) => {
        const state = getState()
        if (state.puzzle) {
            const puzzleGameTree = gameFromPGN(state.puzzle.pgnString)
            actions.initialiseGame({
                puzzleId: state.puzzle.id,
                gameTree: puzzleGameTree,
                isFirstMove: state.puzzle.isFirstMove,
            })
            actions.setPuzzleState(null)

            // opponent first move
            if (!state.puzzle.isFirstMove) {
                const timeout = setTimeout(() => {
                    actions.autoMove()
                }, 1750)
                actions.setOpponentMoveTimeout(timeout)
            }
        }
    }),

    handleHint: thunk(async (actions, userType, { getState }) => {
        const state = getState()
        if (state.availableHints === 0) {
            // show hint limit
            if (userType === GroupNames.SUBSCRIBED) {
                actions.setShowHintLimit(true)
            } else {
                actions.setShowHintLimitDialog(true)
            }
        } else {
            // handle hint
            if (state.customArrows.length === 0) {
                const expectedProgressMove = getMoveById(
                    state.progressGameTree,
                    state.progressCurrentPosition?.nextMoveIds[0],
                )
                if (expectedProgressMove && state.progressCurrentPosition.position.turn === state.myColor) {
                    const nextMoveFrom = nameOfSquare(expectedProgressMove?.move.from)
                    if (!state.hint) {
                        await actions.setHintAsync({ from: nextMoveFrom as Square })
                        actions.setFeedbackType(FeedbackType.HINT_USED)
                    } else {
                        const nextMoveTo = nameOfSquare(expectedProgressMove?.move.to)
                        await actions.setHintAsync({ from: nextMoveFrom as Square, to: nextMoveTo as Square })
                        actions.setFeedbackType(FeedbackType.HINT_USED)
                    }
                }
            }
        }
    }),

    resetAllStates: thunk((actions) => {
        actions.clearAllTimers()
        actions.resetGame()
        actions.clearHint()
        actions.setAttemptsCounter(1)
        actions.setShowSolution(false)
        actions.setUsedShowSolution(false)
        actions.setShowHintLimit(false)
        actions.setJoinUsOpen(false)
        actions.setPuzzleState(null)
        actions.setNotations([])
        actions.setShowHintLimitDialog(false)
        actions.setPuzzle(undefined)
        actions.setFeedbackType(undefined)
        actions.setShowFeedbackDialog(false)
        actions.setFeedbackMessage('')
        actions.setShowStreak(false)
        actions.setPerformanceGroupID(0)
    }),

    establishListener: thunk((actions, payload, { getStoreState, getStoreActions }) => {
        const { ws } = getStoreActions()
        const { isConnected } = getStoreState().ws
        if (isConnected) {
            ws.addListener({
                messageType: MessageType.PUZZLE_FEEDBACK,
                callback: (data) => {
                    const { streak, puzzleId } = getStoreState().dailyPuzzleViewV2
                    console.log('[WS-GATEWAY] Received PUZZLE_FEEDBACK data:', data)
                    if (ENABLE_DAILY_PUZZLE_FEEDBACK) {
                        if (data.streak > streak.currentStreak) {
                            actions.setShowStreak(true)
                        }
                        actions.setFeedbackMessage(data.message)
                        actions.setPerformanceGroupID(data.groupId)
                        // WS not updated yet - keep current currentPuzzleResolved state
                        actions.setStreak({ currentStreak: data.streak, currentPuzzleResolved: streak.currentPuzzleResolved })
                        actions.setShowFeedbackDialog(true)

                        analyticsManager.dispatchEvent('feedbackPopupShown', {
                            puzzleId: puzzleId || '',
                            groupId: data.groupId,
                            percentileRaw: data.percentile,
                            feedbackScore: data.score,
                            streakCount: data.streak,
                            usedShowSolution: data.solutionShown,
                            dynamicText: data.message,
                            feature: 'dailyPuzzle',
                        })
                    } else {
                        if (data.streak > streak.currentStreak) {
                            actions.setShowStreakAnimation(true)
                        }
                        // WS not updated yet - keep current currentPuzzleResolved state
                        actions.setStreak({ currentStreak: data.streak, currentPuzzleResolved: streak.currentPuzzleResolved })
                    }
                },
            })
        }
    }),
}
